// @flow

import React from 'react';
import Hammer from 'hammerjs';
import { connect } from 'react-redux';
import KeepeekMedia from './KeepeekMedia';
import { closeModal } from '../../state/page/pageService';
import type { ModalElements } from '../../types/commonTypes';
import { isMobile } from '../../const';

type Props = {
  openModal: ModalElements,
  closeModal: Function,
}
type State = {
  itemOpened: number,
  showRightArrow: boolean,
  showLeftArrow: boolean,
  isFullScreen: boolean,
  is360: boolean,
}

class ModalMediaCarousel extends React.Component<Props, State> {
  state = {
    itemOpened: this.props.openModal.itemSelected,
    showRightArrow: this.props.openModal.medias.length > 0,
    showLeftArrow: this.props.openModal.medias.length > 0,
    isFullScreen: false,
    is360: false,
  };

  ref;
  shouldInitHammer = true;

  shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    if (nextProps.openModal.isOpen !== this.props.openModal.isOpen) {
      nextState.itemOpened = nextProps.openModal.itemSelected;

      nextState.showRightArrow = nextProps.openModal.medias.length > 1
        && nextProps.openModal.itemSelected < nextProps.openModal.medias.length - 1;

      nextState.showLeftArrow = nextProps.openModal.medias.length > 1
        && nextProps.openModal.itemSelected > 0;

      this.setState({ isFullScreen: false });
    }
    if (nextProps.openModal.isOpen) {
      window.addEventListener('keydown', this.handleArrows);
    } else {
      window.removeEventListener('keydown', this.handleArrows);
      this.shouldInitHammer = true;
    }
    return true;
  }

  onCloseModal = () => {
    this.props.closeModal();
  };

  onExitFullScreen = () => {
    const fullscreen = document.fullscreenElement
      || document.webkitFullscreenElement
      || document.mozFullScreenElement
      || document.msFullscreenElement;
    if (this.state.isFullScreen && !fullscreen) {
      window.removeEventListener('fullscreenchange', this.onExitFullScreen);
      this.setState({ isFullScreen: false });
    }
  };

  onFullScreenError = () => {
    window.removeEventListener('fullscreenchange', this.onExitFullScreen);
    this.setState({ isFullScreen: false });
  };

  onFullScreen = () => {
    const elem = document.getElementsByClassName('picture-container')[0];
    const requestFullScreen = elem.requestFullscreen
      || elem.msRequestFullscreen
      || elem.mozRequestFullScreen
      || elem.webkitRequestFullscreen;
    if (elem && requestFullScreen) {
      this.setState(
        { isFullScreen: true },
        () => {
          window.addEventListener('fullscreenchange', this.onExitFullScreen);
          window.addEventListener('fullscreenerror', this.onFullScreenError);
        },
      );
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullScreen) {
        elem.webkitRequestFullScreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
    }
  };

  updateIndex = (index: number, e: EventHandler) => {
    e.preventDefault();
    let nextIndex = index;
    if (index < 0) {
      nextIndex = 0;
    }
    if (index >= this.props.openModal.medias.length) {
      nextIndex = this.props.openModal.medias.length - 1;
    }
    this.setState({
      itemOpened: nextIndex,
      showLeftArrow: nextIndex > 0,
      showRightArrow: nextIndex < this.props.openModal.medias.length - 1,
    });
  };

  handleArrows = (e: KeyboardEventHandler) => {
    if (e.which === 27) {
      this.onCloseModal();
      return;
    }
    if (e.which === 70) {
      this.onFullScreen();
      return;
    }
    let index = this.state.itemOpened;
    if (e.which === 37) {
      index = this.state.itemOpened - 1;
    } else if (e.which === 39) {
      index = this.state.itemOpened + 1;
    }
    this.updateIndex(index, e);
  };

  render() {
    if (this.props.openModal.medias.length < 1) {
      return false;
    }
    return (
      this.props.openModal.isOpen && this.props.openModal.medias[this.state.itemOpened] && (
        <div
          role="dialog"
          className="media-modal"
          ref={(ref) => {
            if (isMobile && ref && this.shouldInitHammer) {
              this.ref = ref;
              const hammertime = new Hammer(ref);
              hammertime.get('swipe').set({ direction: Hammer.DIRECTION_HORIZONTAL });
              hammertime.on('swiperight', (e: EventHandler) => {
                if (this.state.is360) {
                  return;
                }
                this.updateIndex(this.state.itemOpened - 1, e);
              });
              hammertime.on('swipeleft', (e: EventHandler) => {
                if (this.state.is360) {
                  return;
                }
                this.updateIndex(this.state.itemOpened + 1, e);
              });
              this.shouldInitHammer = false;
            }
          }}
        >
          <div className={`content${this.state.isFullScreen ? ' fullscreen' : ''}`}>
            <div className="left-button">
              {this.state.showLeftArrow &&
              <button
                className="carousel-button"
                onClick={(e: EventHandler) => this.updateIndex(this.state.itemOpened - 1, e)}
              >
                <i className="fas fa-chevron-left icon" />
              </button>
              }
            </div>
            <div className="picture-container">
              {this.props.openModal.isAlreadyLoaded ?
                <KeepeekMedia
                  keepeekMedia={this.props.openModal.medias[this.state.itemOpened]}
                  fullscreen
                  disableClick
                  is360Callback={is360 => this.setState({ is360 })}
                />
                :
                <KeepeekMedia
                  keepeekId={this.props.openModal.medias[this.state.itemOpened].keepeekId}
                  fullscreen
                  disableClick
                  is360Callback={is360 => this.setState({ is360 })}
                />
              }
              <div
                className="close"
                role="button"
                tabIndex={0}
                onKeyDown={(e: KeyboardEventHandler) => {
                  if (e.which === 27) {
                    this.onCloseModal();
                  }
                }}
                onClick={this.onCloseModal}
              >
                <i className="fa fa-times" />
              </div>
              {
                !this.setState.is360 &&
                <div
                  className="fullscreen"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e: KeyboardEventHandler) => {
                    if (e.which === 70) {
                      this.onFullScreen();
                    }
                  }}
                  onClick={this.onFullScreen}
                  hidden={this.state.isFullScreen}
                >
                  <i className="fa fa-expand" />
                </div>
              }
            </div>
            <div className="right-button">
              {this.state.showRightArrow &&
              <button
                className="carousel-button"
                onClick={(e: EventHandler) => this.updateIndex(this.state.itemOpened + 1, e)}
              >
                <i className="fas fa-chevron-right icon" />
              </button>
              }
            </div>
          </div>
        </div>
      )
    );
  }
}

export default connect(
  state => ({
    openModal: state.page.openModal,
  }),
  {
    closeModal,
  },
)(ModalMediaCarousel);
